import React, { useEffect, useState } from "react";
// Kept even though the new markup no longer uses these classes: the rules in
// here are global and several other legacy popups (.input-field, .createRoom*)
// render against them. Removing the import would pull those styles out of the
// bundle for anything that happens to be relying on them.
import "./ResetPasswordPopup.css";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  coworkData,
  postResetPasswordEmail,
  clearCoworkSliceStates,
} from "../../../redux/cowork/coworkSlice";
import { Breaker, Button, Flexbox, InputField } from "../../../MakersV2/Theme/GlobalStyles";
import { Text } from "../../../MakersV2/Theme/Typography";
import { CloseCircle } from "../../../MakersV2/Theme/Icons";
import { globalTheme } from "../../../MakersV2/Theme/Theme";
import { PopupBody, PopupWrapper } from "../../../MakersV2/Components/Popup/CommonPopup";
import { toastOpts } from "../../../utils/toastConfig";

// Rebuilt on the MakersV2 popup shell (PopupWrapper / PopupBody) to match
// AddTowerPopup and LogoutPopup — the previous version was the only popup on
// the login screen still using the legacy Button/InputField and raw
// .popupContainer markup.
const ResetPasswordPopup = ({ isActive, setResetPopup }) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const {
    isCoworkSliceSmallFetching,
    isCoworkSliceSuccess,
    isCoworkSliceError,
    coworkSliceErrorMessage,
  } = useSelector(coworkData);

  // The reset endpoint wants the origin so the emailed link points back at the
  // module the user started from.
  const baseUrl = window.location.origin;

  // Reset the form each time the popup opens, so a previous attempt's value or
  // validation error isn't still sitting there.
  useEffect(() => {
    if (!isActive) return;
    setEmail("");
    setError("");
  }, [isActive]);

  // isCoworkSliceSuccess is a SHARED flag that every cowork thunk sets, and the
  // old code both toasted "Email Sent Successfully" and closed the popup
  // whenever it flipped — so an unrelated cowork request succeeding elsewhere
  // produced a false confirmation. Gate on isActive and clear the flag straight
  // away so this only ever reacts to its own request.
  useEffect(() => {
    if (!isActive || !isCoworkSliceSuccess) return;
    toast.success(
      "Password reset link sent. Check your inbox.",
      toastOpts("reset-password-sent")
    );
    dispatch(clearCoworkSliceStates());
    setResetPopup(false);
  }, [isActive, isCoworkSliceSuccess, dispatch, setResetPopup]);

  useEffect(() => {
    if (!isActive || !isCoworkSliceError) return;
    toast.error(
      coworkSliceErrorMessage || "We couldn't send the reset link. Please try again.",
      toastOpts("reset-password-error")
    );
    dispatch(clearCoworkSliceStates());
  }, [isActive, isCoworkSliceError, coworkSliceErrorMessage, dispatch]);

  const submit = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Email is required.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    dispatch(postResetPasswordEmail({ email: trimmed, baseUrl }));
  };

  return (
    <PopupWrapper className={`${isActive ? "active" : ""}`}>
      <PopupBody maxWidth={480} padding={24}>
        <Flexbox spaceBW alignCenter>
          <Text font18 inter semiBold white maxWidth="max-content">
            Reset Password
          </Text>
          <CloseCircle className={"pointer"} onClick={() => setResetPopup(false)} />
        </Flexbox>

        <Breaker
          marginTop={16}
          marginBottom={20}
          borderColor={globalTheme.colors.white1}
        />

        <Text font14 inter regular grey3 className="mb20">
          Enter the email address registered to your account and we'll send you a
          link to set a new password.
        </Text>

        <InputField
          label="Email Id"
          isImp
          type="email"
          name="email"
          placeholder="Enter registered email Id"
          value={email}
          error={error}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />

        <Flexbox alignCenter gap={12} justiCenter className="mt20 respBtnRow">
          <Button
            whiteFilled
            buttonMd
            name={"Cancel"}
            disabled={isCoworkSliceSmallFetching}
            onClick={() => setResetPopup(false)}
          />
          {/* MakersV2's Button has no loading prop — it destructures a fixed
              list, so isWaiting/isLoading are silently dropped (AddTowerPopup
              passes one that does nothing). Swap the label instead so the
              in-flight state is actually visible. */}
          <Button
            primaryFilled
            buttonMd
            name={isCoworkSliceSmallFetching ? "Sending…" : "Send Reset Link"}
            onClick={submit}
            disabled={isCoworkSliceSmallFetching}
          />
        </Flexbox>
      </PopupBody>
    </PopupWrapper>
  );
};

export default ResetPasswordPopup;
